import { useState, useRef, useEffect, useMemo } from 'react';
import { useStadtList } from '../../api/stadt';
import type { Stadt } from '../../types/stadt';
import { StadtModal } from './StadtModal';

interface StadtSelectProps {
  value: string;
  onChange: (id: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  id?: string;
}

export function StadtSelect({
  value,
  onChange,
  label,
  placeholder = '-- Bitte wählen --',
  error,
  disabled = false,
  id,
}: StadtSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStadt, setEditingStadt] = useState<Stadt | undefined>(undefined);
  const [initialName, setInitialName] = useState('');
  // Store newly created/edited city until query refreshes
  const [pendingCity, setPendingCity] = useState<Stadt | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: cities, isLoading } = useStadtList(true);

  // Find selected city - check pending city first, then the list
  const selectedCity = useMemo(() => {
    if (pendingCity && pendingCity.id === value) {
      return pendingCity;
    }
    return cities?.find((c) => c.id === value);
  }, [cities, value, pendingCity]);

  // Clear pending city once it appears in the refetched list
  useEffect(() => {
    if (pendingCity && cities?.some((c) => c.id === pendingCity.id)) {
      setPendingCity(null);
    }
  }, [cities, pendingCity]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!cities) return [];
    if (!searchText.trim()) return cities;
    const search = searchText.toLowerCase().trim();
    return cities.filter((city) => city.name.toLowerCase().includes(search));
  }, [cities, searchText]);

  // Check if exact match exists
  const exactMatchExists = useMemo(() => {
    if (!searchText.trim()) return true;
    const search = searchText.toLowerCase().trim();
    return cities?.some((city) => city.name.toLowerCase() === search) ?? false;
  }, [cities, searchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchText('');
    } else if (e.key === 'Enter' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleSelectCity = (city: Stadt) => {
    onChange(city.id);
    setIsOpen(false);
    setSearchText('');
  };

  const handleEditCity = (e: React.MouseEvent, city: Stadt) => {
    e.stopPropagation();
    setEditingStadt(city);
    setInitialName('');
    setIsOpen(false);
    setModalOpen(true);
  };

  const handleCreateCity = () => {
    setEditingStadt(undefined);
    setInitialName(searchText.trim());
    setIsOpen(false);
    setModalOpen(true);
  };

  const handleModalSuccess = (stadt: Stadt) => {
    setPendingCity(stadt);
    onChange(stadt.id);
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchText('');
  };

  const inputClassName = `
    block w-full rounded-md shadow-sm sm:text-sm
    ${error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
    }
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
  `;

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* Input field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={isOpen ? searchText : (selectedCity?.name || '')}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClassName}
          autoComplete="off"
        />

        {/* Clear button */}
        {value && !isOpen && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-8 flex items-center pr-2 text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Laden...</div>
          ) : (
            <>
              {/* City options */}
              {filteredCities.length > 0 ? (
                <ul className="py-1">
                  {filteredCities.map((city) => (
                    <li key={city.id}>
                      <div
                        className={`
                          flex items-center justify-between px-4 py-2 cursor-pointer
                          ${city.id === value ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'}
                        `}
                        onClick={() => handleSelectCity(city)}
                      >
                        <span className="text-sm">{city.name}</span>
                        <button
                          type="button"
                          onClick={(e) => handleEditCity(e, city)}
                          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Stadt bearbeiten"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  Keine Städte gefunden
                </div>
              )}

              {/* Create new city option */}
              {searchText.trim() && !exactMatchExists && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCreateCity}
                    className="w-full flex items-center px-4 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    "{searchText.trim()}" erstellen
                  </button>
                </div>
              )}

              {/* Always show create option at the bottom */}
              {(!searchText.trim() || exactMatchExists) && (
                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleCreateCity}
                    className="w-full flex items-center px-4 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Neue Stadt erstellen
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal for create/edit */}
      <StadtModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleModalSuccess}
        editingStadt={editingStadt}
        initialName={initialName}
      />
    </div>
  );
}
